import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useMemo } from 'react';
import { TAppointmentProjectMinimal } from './types';

interface ProjectOption {
  id: number;
  title: string;
}

interface AppointmentProjectSelectorProps {
  selectedProjectId: number | undefined;
  onProjectChange: (projectId: number | undefined) => void;
  projects: TAppointmentProjectMinimal[];
  disabled?: boolean;
  error?: boolean;
  helperText?: string | undefined;
  t: (key: string) => string;
}

export const AppointmentProjectSelector: React.FC<AppointmentProjectSelectorProps> = ({
  selectedProjectId,
  onProjectChange,
  projects,
  disabled = false,
  error = false,
  helperText,
  t,
}) => {
  // Create project options without "All Projects" option
  const projectOptions = useMemo(() => {
    return projects.map((project: TAppointmentProjectMinimal) => ({
      id: project.id,
      title: project.title,
    }));
  }, [projects]);

  // Find selected project option
  const selectedProjectOption =
    projectOptions.find((option: ProjectOption) => option.id === selectedProjectId) || null;

  // Handle project selection change
  const handleProjectChange = (_: React.SyntheticEvent, newValue: ProjectOption | null) => {
    onProjectChange(newValue?.id || undefined);
  };

  return (
    <Stack spacing={1} width="100%">
      <Typography variant="body2" fontWeight={500} color="text.secondary">
        {t('landing.scheduling.appointment.selectProject')}
      </Typography>
      <Autocomplete
        value={selectedProjectOption}
        onChange={handleProjectChange}
        options={projectOptions}
        disabled={disabled || projectOptions.length === 1}
        size="small"
        sx={{ width: '100%' }}
        getOptionLabel={(option) => option.title}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('landing.scheduling.appointment.selectProjectPlaceholder')}
            data-treedistestid="appointment-project-selector-input"
            error={error}
            helperText={helperText}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.id}
            data-treedistestid={`appointment-project-option-${option.id}`}
          >
            {option.title}
          </li>
        )}
        noOptionsText={t('scheduling.noProjectsFound')}
      />
    </Stack>
  );
};
